/**
 * HotelsMobileCarousel — 3D carousel for hotel rooms on mobile during voice calls.
 *
 * Adapted from MobileProductCarousel with hotel-specific card markup:
 *   - "Book Now" button instead of "Add to Cart"
 *   - Nights stepper instead of quantity/weight
 *   - No variant picker support
 *
 * Floats above the minimized voice bar (appended to document.body).
 * Uses CSS 3D perspective transforms for a rotating wheel effect.
 */
export class HotelsMobileCarousel {
  constructor(options = {}) {
    this.onRoomBook = options.onRoomBook || (() => {});
    this.onClose = options.onClose || (() => {});
    this.primaryColor = options.primaryColor || '#2563eb';

    this.rooms = [];
    this.current = 0;
    this.nights = [];

    this.container = null;
    this.wheel = null;
    this.dotsEl = null;

    this.anglePerCard = 42;
    this.radius = 240;

    this.dragging = false;
    this.startX = 0;
    this.swiped = false;
    this.dragThreshold = 40;

    this._boundTouchMove = this._onTouchMove.bind(this);
    this._boundTouchEnd = this._onTouchEnd.bind(this);
    this._boundMouseMove = this._onMouseMove.bind(this);
    this._boundMouseUp = this._onMouseUp.bind(this);
  }

  show(rooms, title) {
    this.hide();
    if (!rooms || rooms.length === 0) return;

    this._normalizeRooms(rooms);
    this.current = 0;

    this._injectStyles();
    this._buildDOM(title);
    this._buildCards();
    this._buildDots();
    this._setupDragEvents();
    this._updateWheel();

    document.body.appendChild(this.container);
    this._positionAboveMobileBar();
  }

  hide() {
    this._removeEventListeners();
    if (this.container?.parentNode) {
      this.container.classList.add('ttp-carousel--hiding');
      const c = this.container;
      setTimeout(() => { if (c.parentNode) c.parentNode.removeChild(c); }, 280);
    }
    this.container = null;
    this.wheel = null;
    this.dotsEl = null;
  }

  destroy() {
    this._removeEventListeners();
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    const toast = document.getElementById('ttpHotelCarouselToast');
    if (toast) toast.remove();
    const style = document.getElementById('ttp-hotel-carousel-styles');
    if (style) style.remove();
  }

  showToast(roomName) {
    const existing = document.getElementById('ttpHotelCarouselToast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'ttpHotelCarouselToast';
    toast.className = 'ttp-carousel-toast';
    toast.innerHTML = `
      <div class="ttp-carousel-toast__icon">\u2713</div>
      <div class="ttp-carousel-toast__text">
        <strong>${roomName}</strong> booked!
      </div>
    `;
    document.body.appendChild(toast);

    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    toast.style.bottom = mobileBar ? `${mobileBar.offsetHeight + 12}px` : '100px';

    setTimeout(() => {
      toast.classList.add('ttp-carousel-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  _normalizeRooms(rooms) {
    this.rooms = rooms.map(r => ({
      ...r,
      imageUrl: r.imageUrl || r.image_url || '',
      available: r.available !== undefined ? r.available : true,
      pricePerNight: r.pricePerNight || r.price_per_night || r.price || 0,
      currency: r.currency || 'USD',
      name: r.name || r.roomName || 'Room',
    }));
    this.nights = this.rooms.map(r => r.nights || 1);
  }

  _buildDOM(title) {
    this.container = document.createElement('div');
    this.container.className = 'ttp-mobile-carousel';
    this.container.id = 'ttpHotelMobileCarousel';
    this.container.style.setProperty('--ttp-carousel-primary', this.primaryColor);
    this.container.style.setProperty('--ttp-carousel-primary-light', this._hexToRgba(this.primaryColor, 0.12));
    this.container.style.setProperty('--ttp-carousel-primary-dark', this._darkenHex(this.primaryColor, 30));
    this.container.style.setProperty('--ttp-carousel-primary-shadow', this._hexToRgba(this.primaryColor, 0.3));

    const header = document.createElement('div');
    header.className = 'ttp-carousel-header';
    header.innerHTML = `
      <span class="ttp-carousel-title">${title || 'Available Rooms'}</span>
      <button class="ttp-carousel-close" aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `;
    header.querySelector('.ttp-carousel-close').addEventListener('click', () => {
      this.hide();
      this.onClose();
    });

    const area = document.createElement('div');
    area.className = 'ttp-carousel-area';

    this.wheel = document.createElement('div');
    this.wheel.className = 'ttp-carousel-wheel';
    area.appendChild(this.wheel);

    this.dotsEl = document.createElement('div');
    this.dotsEl.className = 'ttp-carousel-dots';

    this.container.appendChild(header);
    this.container.appendChild(area);
    this.container.appendChild(this.dotsEl);
  }

  _buildCards() {
    this.rooms.forEach((r, i) => {
      const card = document.createElement('div');
      card.className = 'ttp-carousel-card' + (!r.available ? ' ttp-carousel-card--oos' : '');
      card.dataset.index = i;

      const sym = r.currency === 'ILS' ? '\u20AA' : (r.currency === 'EUR' ? '\u20AC' : '$');
      const total = (r.pricePerNight * this.nights[i]).toFixed(0);

      card.innerHTML = `
        <div class="ttp-carousel-card__image">
          <img src="${r.imageUrl}" alt="${r.name}" onerror="this.style.display='none'" />
          ${!r.available ? '<div class="ttp-carousel-card__badge">Unavailable</div>' : ''}
        </div>
        <div class="ttp-carousel-card__info">
          <div class="ttp-carousel-card__name">${r.name}</div>
          ${r.subtitle ? `<div class="ttp-carousel-card__unit">${r.subtitle}</div>` : ''}
          <div class="ttp-carousel-card__price">${sym}${r.pricePerNight}<span style="font-size:10px;color:#94a3b8;font-weight:400">/night</span></div>
        </div>
        <div class="ttp-carousel-card__actions">
          <div class="ttp-carousel-card__stepper">
            <button class="ttp-carousel-card__qty-btn ttp-carousel-minus" data-idx="${i}" ${!r.available || this.nights[i] <= 1 ? 'disabled' : ''}>−</button>
            <span class="ttp-carousel-card__qty-val" id="ttpHNights${i}">${this.nights[i]} night${this.nights[i] !== 1 ? 's' : ''}</span>
            <button class="ttp-carousel-card__qty-btn ttp-carousel-plus" data-idx="${i}" ${!r.available ? 'disabled' : ''}>+</button>
          </div>
          <button class="ttp-carousel-card__add-btn" data-idx="${i}" ${!r.available ? 'disabled' : ''}>
            ${r.available ? `Book Now \u00B7 ${sym}${total}` : 'Unavailable'}
          </button>
        </div>
      `;

      card.style.transform = `rotateY(${i * this.anglePerCard}deg) translateZ(${this.radius}px)`;
      this.wheel.appendChild(card);
    });

    this._setupCardHandlers();
  }

  _setupCardHandlers() {
    const area = this.container.querySelector('.ttp-carousel-area');
    if (!area) return;

    let tapOrigin = null;

    area.addEventListener('touchstart', (e) => {
      tapOrigin = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, { passive: true });

    area.addEventListener('touchend', (e) => {
      if (!tapOrigin || this.swiped) { tapOrigin = null; return; }
      const t = e.changedTouches[0];
      const dist = Math.hypot(t.clientX - tapOrigin.x, t.clientY - tapOrigin.y);
      if (dist < 15) {
        this._handleTapAt(t.clientX, t.clientY);
      }
      tapOrigin = null;
    });

    area.addEventListener('click', (e) => {
      const minus = e.target.closest('.ttp-carousel-minus');
      const plus = e.target.closest('.ttp-carousel-plus');
      const addBtn = e.target.closest('.ttp-carousel-card__add-btn');

      if (minus && !minus.disabled) {
        e.stopPropagation();
        this._handleMinus();
      } else if (plus && !plus.disabled) {
        e.stopPropagation();
        this._handlePlus();
      } else if (addBtn && !addBtn.disabled) {
        e.stopPropagation();
        this._handleBook();
      }
    });
  }

  _handleTapAt(x, y) {
    const cards = this.wheel.querySelectorAll('.ttp-carousel-card');
    const currentCard = cards[this.current];
    if (!currentCard) return;

    const minus = currentCard.querySelector('.ttp-carousel-minus');
    const plus = currentCard.querySelector('.ttp-carousel-plus');
    const addBtn = currentCard.querySelector('.ttp-carousel-card__add-btn');

    if (this._hitTest(x, y, minus) && !minus?.disabled) {
      this._handleMinus();
    } else if (this._hitTest(x, y, plus) && !plus?.disabled) {
      this._handlePlus();
    } else if (this._hitTest(x, y, addBtn) && !addBtn?.disabled) {
      this._handleBook();
    }
  }

  _hitTest(x, y, el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  _handleMinus() {
    const idx = this.current;
    if (this.nights[idx] > 1) {
      this.nights[idx]--;
      this._refreshStepper(idx);
    }
  }

  _handlePlus() {
    const idx = this.current;
    if (this.nights[idx] < 30) {
      this.nights[idx]++;
      this._refreshStepper(idx);
    }
  }

  _handleBook() {
    const idx = this.current;
    const room = this.rooms[idx];
    if (!room.available) return;

    const cards = this.wheel.querySelectorAll('.ttp-carousel-card');
    const cardEl = cards[idx];
    const btn = cardEl?.querySelector('.ttp-carousel-card__add-btn');
    if (btn) {
      btn.textContent = '\u2713 Booked!';
      btn.classList.add('ttp-carousel-card__add-btn--added');
      btn.disabled = true;
    }

    const roomData = {
      ...room,
      selectedNights: this.nights[idx],
    };

    this.onRoomBook(cardEl, roomData);
    setTimeout(() => this.hide(), 800);
  }

  _refreshStepper(idx) {
    const el = this.container.querySelector(`#ttpHNights${idx}`);
    const minusBtn = this.container.querySelector(`.ttp-carousel-minus[data-idx="${idx}"]`);
    const addBtn = this.container.querySelector(`.ttp-carousel-card__add-btn[data-idx="${idx}"]`);
    if (el) {
      el.textContent = `${this.nights[idx]} night${this.nights[idx] !== 1 ? 's' : ''}`;
    }
    if (minusBtn) {
      minusBtn.disabled = this.nights[idx] <= 1;
    }
    if (addBtn && !addBtn.classList.contains('ttp-carousel-card__add-btn--added')) {
      const room = this.rooms[idx];
      const sym = room.currency === 'ILS' ? '\u20AA' : (room.currency === 'EUR' ? '\u20AC' : '$');
      const total = (room.pricePerNight * this.nights[idx]).toFixed(0);
      addBtn.textContent = `Book Now \u00B7 ${sym}${total}`;
    }
  }

  _buildDots() {
    if (this.rooms.length <= 1) return;
    this.rooms.forEach((_, i) => {
      const dot = document.createElement('div');
      dot.className = 'ttp-carousel-dot';
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsEl.appendChild(dot);
    });
  }

  _updateWheel() {
    const rotation = -(this.current * this.anglePerCard);
    this.wheel.style.transform = `translateZ(-${this.radius}px) rotateY(${rotation}deg)`;

    const cards = this.wheel.querySelectorAll('.ttp-carousel-card');
    cards.forEach((card, i) => {
      let eff = (i * this.anglePerCard) + rotation;
      while (eff > 180) eff -= 360;
      while (eff < -180) eff += 360;

      if (Math.abs(eff) > 90) {
        card.style.visibility = 'hidden';
        card.style.opacity = '0';
      } else {
        card.style.visibility = 'visible';
        card.style.opacity = this.rooms[i].available ? '1' : '0.7';
      }
    });

    const dots = this.dotsEl.querySelectorAll('.ttp-carousel-dot');
    dots.forEach((dot, i) => dot.classList.toggle('ttp-carousel-dot--active', i === this.current));
  }

  goTo(i) {
    this.current = Math.max(0, Math.min(this.rooms.length - 1, i));
    this._updateWheel();
  }

  _setupDragEvents() {
    const area = this.container.querySelector('.ttp-carousel-area');
    if (!area) return;

    area.addEventListener('touchstart', (e) => {
      this.dragging = true;
      this.startX = e.touches[0].clientX;
      this.swiped = false;
    }, { passive: true });

    area.addEventListener('mousedown', (e) => {
      this.dragging = true;
      this.startX = e.clientX;
      this.swiped = false;
    });

    document.addEventListener('touchmove', this._boundTouchMove, { passive: true });
    document.addEventListener('touchend', this._boundTouchEnd);
    document.addEventListener('mousemove', this._boundMouseMove);
    document.addEventListener('mouseup', this._boundMouseUp);
  }

  _onTouchMove(e) {
    if (!this.dragging) return;
    const diff = e.touches[0].clientX - this.startX;
    if (Math.abs(diff) > this.dragThreshold) {
      this.goTo(this.current + (diff > 0 ? -1 : 1));
      this.startX = e.touches[0].clientX;
      this.swiped = true;
    }
  }

  _onTouchEnd() {
    this.dragging = false;
    if (this.swiped) setTimeout(() => { this.swiped = false; }, 400);
  }

  _onMouseMove(e) {
    if (!this.dragging) return;
    const diff = e.clientX - this.startX;
    if (Math.abs(diff) > this.dragThreshold) {
      this.goTo(this.current + (diff > 0 ? -1 : 1));
      this.startX = e.clientX;
      this.swiped = true;
    }
  }

  _onMouseUp() {
    this.dragging = false;
    if (this.swiped) setTimeout(() => { this.swiped = false; }, 400);
  }

  _removeEventListeners() {
    document.removeEventListener('touchmove', this._boundTouchMove);
    document.removeEventListener('touchend', this._boundTouchEnd);
    document.removeEventListener('mousemove', this._boundMouseMove);
    document.removeEventListener('mouseup', this._boundMouseUp);
  }

  _positionAboveMobileBar() {
    requestAnimationFrame(() => {
      if (!this.container) return;
      const bar = document.getElementById('mobile-voice-call-bar-container');
      if (bar) {
        const barRect = bar.getBoundingClientRect();
        this.container.style.bottom = `${window.innerHeight - barRect.top + 58}px`;
      }
    });
  }

  _hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  _darkenHex(hex, amount) {
    const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
    const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
    const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  _injectStyles() {
    if (document.getElementById('ttp-hotel-carousel-styles')) return;
    const s = document.createElement('style');
    s.id = 'ttp-hotel-carousel-styles';
    s.textContent = this._generateCSS();
    document.head.appendChild(s);
  }

  _generateCSS() {
    // Reuse ecommerce carousel styles — they are class-identical
    return `
/* ═══════════════════════════════════════
   HOTEL MOBILE CAROUSEL (reuses ecommerce carousel classes)
   ═══════════════════════════════════════ */

.ttp-mobile-carousel {
  position: fixed !important;
  bottom: 180px;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  z-index: 2147483646 !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  animation: ttpCarouselIn 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  -webkit-tap-highlight-color: transparent;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 16px;
}

.ttp-mobile-carousel > .ttp-carousel-header,
.ttp-mobile-carousel > .ttp-carousel-area,
.ttp-mobile-carousel > .ttp-carousel-dots {
  position: relative;
  z-index: 1;
}

.ttp-mobile-carousel::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 420px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: 24px 24px 0 0;
  pointer-events: none;
  z-index: 0;
}

.ttp-mobile-carousel.ttp-carousel--hiding {
  animation: ttpCarouselOut 0.28s ease forwards;
  pointer-events: none;
}

@keyframes ttpCarouselIn {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ttpCarouselOut {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(30px); }
}

.ttp-carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px 8px;
  flex-shrink: 0;
}

.ttp-carousel-title {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0,0,0,0.4);
}

.ttp-carousel-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0;
  background: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0.85;
}
.ttp-carousel-close:active { opacity: 1; }

.ttp-carousel-area {
  position: relative;
  height: 300px;
  flex-shrink: 0;
  perspective: 800px;
  perspective-origin: 50% 55%;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}
.ttp-carousel-area:active { cursor: grabbing; }

.ttp-carousel-wheel {
  position: absolute;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}

.ttp-carousel-card {
  position: absolute;
  width: 150px;
  height: 285px;
  left: 50%;
  top: 50%;
  margin-left: -75px;
  margin-top: -142px;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  transform-style: preserve-3d;
  transition: visibility 0.3s, opacity 0.3s;
  display: flex;
  flex-direction: column;
}

.ttp-carousel-card--oos { opacity: 0.7; }

.ttp-carousel-card__image {
  width: 100%;
  height: 100px;
  background: linear-gradient(145deg, #f0f4ff, #e8ecf8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.ttp-carousel-card__image img {
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
}

.ttp-carousel-card__badge {
  position: absolute;
  top: 6px; right: 6px;
  background: #ef4444;
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
}

.ttp-carousel-card__info {
  padding: 8px 10px 4px;
  flex-shrink: 0;
}

.ttp-carousel-card__name {
  font-size: 12px;
  font-weight: 600;
  color: #1e1b4b;
  line-height: 1.25;
  margin-bottom: 1px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ttp-carousel-card__unit {
  font-size: 9px;
  color: #64748b;
  margin-bottom: 3px;
}

.ttp-carousel-card__price {
  font-size: 16px;
  font-weight: 700;
  color: var(--ttp-carousel-primary, #2563eb);
}

.ttp-carousel-card__actions {
  padding: 6px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
}

.ttp-carousel-card__stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ttp-carousel-primary-light, rgba(37,99,235,0.12));
  border-radius: 10px;
  padding: 3px;
}

.ttp-carousel-card__qty-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: var(--ttp-carousel-primary, #2563eb);
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  touch-action: manipulation;
}
.ttp-carousel-card__qty-btn:active:not(:disabled) {
  background: var(--ttp-carousel-primary-light);
}
.ttp-carousel-card__qty-btn:disabled { opacity: 0.3; cursor: default; }

.ttp-carousel-card__qty-val {
  min-width: 40px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #1e1b4b;
  user-select: none;
}

.ttp-carousel-card__add-btn {
  width: 100%;
  padding: 9px 12px;
  background: linear-gradient(135deg, var(--ttp-carousel-primary, #2563eb), var(--ttp-carousel-primary-dark, #1d4ed8));
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px var(--ttp-carousel-primary-shadow, rgba(37,99,235,0.3));
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  touch-action: manipulation;
}
.ttp-carousel-card__add-btn:active:not(:disabled) { transform: scale(0.97); }
.ttp-carousel-card__add-btn:disabled { background: #94a3b8; cursor: default; box-shadow: none; }
.ttp-carousel-card__add-btn--added {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
}

.ttp-carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 8px 0 0;
  flex-shrink: 0;
}

.ttp-carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.35);
  cursor: pointer;
  transition: all 0.3s;
}

.ttp-carousel-dot--active {
  width: 24px;
  border-radius: 4px;
  background: #fff;
}

.ttp-carousel-toast {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 100px;
  z-index: 100001;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  animation: ttpCarouselToastIn 0.3s ease;
}

.ttp-carousel-toast--hiding {
  animation: ttpCarouselOut 0.3s ease forwards;
}

@keyframes ttpCarouselToastIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

.ttp-carousel-toast__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.ttp-carousel-toast__text {
  font-size: 13px;
  color: #1e293b;
  line-height: 1.3;
}
.ttp-carousel-toast__text strong { font-weight: 600; }
`;
  }
}
